import React, { useEffect, useMemo, useState } from 'react';

const PACKAGE_ORDER = ['free', 'basic', 'standard', 'premium', 'enterprise'];

const formatNumber = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toLocaleString();
};

const formatVnd = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 'POA';
  return `${formatNumber(numeric)} VND`;
};

const formatSubscription = (value) => {
  const normalized = String(value || 'free');
  return normalized
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatIncludedLabel = (key) => {
  if (key === 'sites') return 'site';
  if (key === 'screens') return "tv's";
  if (key === 'musicPlayers') return 'music';
  if (key === 'websites') return 'website';
  if (key === 'wifi') return 'wi-fi';
  if (key === 'menuQrs') return 'menu';
  if (key === 'productQrs') return 'product';
  if (key === 'serviceQrs') return 'service';
  if (key === 'infoQrs') return 'info';
  return key;
};

const formatIsoDate = (value) => {
  const normalized = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return '—';
  const parsed = new Date(`${normalized}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const openDownloadUrl = (downloadUrl) => {
  const normalized = String(downloadUrl || '').trim();
  if (!normalized || typeof document === 'undefined') return;
  const anchor = document.createElement('a');
  anchor.href = normalized;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

function MySubscriptionSettings({ refreshEntitlements = null }) {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedPackagesBySiteId, setSelectedPackagesBySiteId] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users/me/subscription-summary', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to load subscription summary');
      setSummary(data.summary || null);
      setCurrentUser(data.currentUser || null);
      setSelectedPackagesBySiteId(
        Object.fromEntries((data.summary?.sites || []).map((site) => [site.siteId, site.packageKey]))
      );
    } catch (err) {
      setError(err.message || 'Failed to load subscription summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const packageCards = useMemo(() => {
    const catalog = summary?.packageCatalog || {};
    return PACKAGE_ORDER
      .filter((tier) => catalog[tier])
      .map((tier) => ({
        ...catalog[tier],
      }));
  }, [summary]);

  const handleSiteChange = async (siteId, targetTier) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/users/me/subscription-sites/${siteId}/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ packageKey: targetTier }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const overageSummary = Array.isArray(data?.overages) && data.overages.length
          ? ` ${data.overages.map((entry) => `${entry.label}: ${entry.used}/${entry.limit}`).join(' • ')}`
          : '';
        throw new Error((data?.message || 'Failed to change subscription') + overageSummary);
      }

      if (data?.token) {
        localStorage.setItem('token', data.token);
        if (typeof refreshEntitlements === 'function') {
          await refreshEntitlements();
        }
      }
      setSummary(data.summary || null);
      setSelectedPackagesBySiteId(
        Object.fromEntries((data.summary?.sites || []).map((site) => [site.siteId, site.packageKey]))
      );
      setMessage(data?.message || 'Site package updated');
    } catch (err) {
      setError(err.message || 'Failed to change site package');
    } finally {
      setSaving(false);
    }
  };

  const cancelScheduledChange = async (siteId) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/users/me/subscription-sites/${siteId}/change`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to cancel scheduled change');
      setSummary(data.summary || null);
      setSelectedPackagesBySiteId(
        Object.fromEntries((data.summary?.sites || []).map((site) => [site.siteId, site.packageKey]))
      );
      setMessage(data?.message || 'Scheduled change canceled');
    } catch (err) {
      setError(err.message || 'Failed to cancel scheduled change');
    } finally {
      setSaving(false);
    }
  };

  const billingProfile = summary?.billingProfile || {};
  const runtimeLimits = summary?.runtimeLimits || { limits: {}, used: {}, overages: [], isOverLimit: false };
  const invoiceSchedule = summary?.invoiceSchedule || {};
  const canManageSubscription = Boolean(currentUser?.canManageSubscription);

  return (
    <div style={{ padding: 16, color: '#e5e7eb' }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 6px' }}>My Subscriptions</h3>
        <div style={{ color: '#9ca3af', fontSize: 14 }}>
          View your current account plan, billing cadence, saved documents, and any scheduled plan changes.
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
          {message}
        </div>
      )}

      {loading && <div style={{ color: '#9ca3af' }}>Loading subscription details…</div>}

      {!loading && summary && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ padding: 16, borderRadius: 12, border: '1px solid #374151', background: '#111827', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#93c5fd' }}>Account</div>
                <div style={{ marginTop: 4, fontSize: 22, fontWeight: 700 }}>{summary.email}</div>
                <div style={{ marginTop: 6, color: '#cbd5e1', fontSize: 14 }}>
                  Runtime plan: <strong>{formatSubscription(summary.currentSubscriptionType)}</strong>
                </div>
                <div style={{ marginTop: 4, color: '#9ca3af', fontSize: 13 }}>
                  Billed site mix: {Object.entries(summary.packageCounts || {})
                    .filter(([, count]) => Number(count || 0) > 0)
                    .map(([tier, count]) => `${formatNumber(count)} ${formatSubscription(tier)}`)
                    .join(' • ') || 'No sites yet'}
                </div>
              </div>
              <div style={{ minWidth: 260, display: 'grid', gap: 6, fontSize: 13, color: '#cbd5e1' }}>
                <div><strong>Billing cadence:</strong> {formatNumber(invoiceSchedule.cadenceMonths || 0)} month{Number(invoiceSchedule.cadenceMonths || 0) === 1 ? '' : 's'}</div>
                <div><strong>Current period:</strong> {formatIsoDate(invoiceSchedule.periodStart)} - {formatIsoDate(invoiceSchedule.periodEndInclusive)}</div>
                <div><strong>Next billing cycle:</strong> {formatIsoDate(invoiceSchedule.nextDueDate)}</div>
                <div><strong>Billing entity:</strong> {billingProfile.legalBusinessName || 'Not yet completed'}</div>
                <div><strong>Tax code:</strong> {billingProfile.taxCode || '—'}</div>
                <div><strong>Billing email:</strong> {billingProfile.billingEmail || '—'}</div>
              </div>
            </div>

            {runtimeLimits.isOverLimit && (
              <div style={{ padding: 12, borderRadius: 10, border: '1px solid #92400e', background: 'rgba(146,64,14,0.18)', color: '#fde68a', fontSize: 13 }}>
                This account is currently outside its live device limits: {runtimeLimits.overages.map((entry) => `${entry.label} ${entry.used}/${entry.limit}`).join(' • ')}
              </div>
            )}

            {!canManageSubscription && (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                Only the account manager can change site packages. You can still download billing documents below.
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
            {packageCards.map((pkg) => {
              return (
                <div
                  key={pkg.key}
                  style={{
                    padding: 16,
                    borderRadius: 14,
                    border: '1px solid #374151',
                    background: '#111827',
                    display: 'grid',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{pkg.label}</div>
                      <div style={{ marginTop: 4, fontSize: 13, color: '#cbd5e1' }}>
                        {pkg.basePrice == null ? 'POA' : formatVnd(pkg.basePrice)} / site / month
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {pkg.advertisingEnabled ? (
                        <span style={{ padding: '5px 9px', borderRadius: 999, background: 'rgba(16,185,129,0.18)', color: '#a7f3d0', fontSize: 11, fontWeight: 700 }}>
                          Ads On
                        </span>
                      ) : (
                        <span style={{ padding: '5px 9px', borderRadius: 999, background: 'rgba(148,163,184,0.18)', color: '#cbd5e1', fontSize: 11, fontWeight: 700 }}>
                          Ads Off
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: 13, color: '#9ca3af', minHeight: 36 }}>{pkg.summary}</div>
                  <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
                    {pkg.websitePolicy}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
                    {pkg.aiPolicy} {pkg.apiPolicy}
                  </div>
                  <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                    Includes: {Object.entries(pkg.included || {})
                      .filter(([, count]) => Number(count || 0) > 0)
                      .map(([key, count]) => `${formatNumber(count)} ${formatIncludedLabel(key)}`)
                      .join(' • ')}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ padding: 16, borderRadius: 12, border: '1px solid #374151', background: '#111827', display: 'grid', gap: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Sites in your account</div>
            {(summary.sites || []).length ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {summary.sites.map((site) => (
                  <div
                    key={site.siteId}
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      border: '1px solid #374151',
                      background: '#0f172a',
                      display: 'grid',
                      gap: 10,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{site.siteName}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>{formatSubscription(site.siteType)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ padding: '5px 9px', borderRadius: 999, border: '1px solid #334155', background: '#111827', color: '#e5e7eb', fontSize: 11, fontWeight: 700 }}>
                          {site.packageLabel}
                        </span>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                          {site.basePrice == null ? 'POA' : `${formatVnd(site.basePrice)} / month`}
                        </span>
                        {site.subscriptionChangeRequest && (
                          <span style={{ padding: '5px 9px', borderRadius: 999, background: 'rgba(245,158,11,0.18)', color: '#fcd34d', fontSize: 11, fontWeight: 700 }}>
                            Scheduled {formatIsoDate(site.subscriptionChangeRequest.effectiveOn)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 280px) minmax(0, 1fr)', gap: 12, alignItems: 'start' }}>
                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#cbd5e1' }}>Selected package</span>
                        <select
                          value={selectedPackagesBySiteId[site.siteId] || site.packageKey}
                          onChange={(e) => setSelectedPackagesBySiteId((prev) => ({ ...prev, [site.siteId]: e.target.value }))}
                          disabled={!canManageSubscription || saving}
                          style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
                        >
                          {PACKAGE_ORDER.map((tier) => (
                            <option key={`${site.siteId}:${tier}`} value={tier}>
                              {formatSubscription(tier)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div style={{ display: 'grid', gap: 8 }}>
                        {(() => {
                          const selectedPackageKey = selectedPackagesBySiteId[site.siteId] || site.packageKey;
                          const changeOption = (site.changeOptions || []).find((entry) => entry.tier === selectedPackageKey) || null;
                          return (
                            <>
                              {changeOption?.reason ? (
                                <div style={{ padding: 10, borderRadius: 10, border: '1px solid #4b5563', background: 'rgba(15,23,42,0.65)', fontSize: 12, color: '#cbd5e1' }}>
                                  {changeOption.reason}
                                </div>
                              ) : (
                                <div style={{ padding: 10, borderRadius: 10, border: '1px solid #1f2937', background: 'rgba(15,23,42,0.45)', fontSize: 12, color: '#9ca3af' }}>
                                  {changeOption?.action === 'upgrade'
                                    ? 'Upgrades apply immediately and update the account runtime tier if this becomes the highest package in use.'
                                    : changeOption?.action === 'downgrade'
                                      ? `Downgrades take effect on the next billing cycle: ${formatIsoDate(changeOption?.effectiveOn)}.`
                                      : 'Select a package to see change details.'}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                {changeOption?.action === 'contact' ? (
                                  <button type="button" disabled>
                                    Contact support
                                  </button>
                                ) : changeOption?.action === 'current' ? (
                                  <button type="button" disabled>
                                    Current package
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={!canManageSubscription || !changeOption?.available || saving}
                                    onClick={() => handleSiteChange(site.siteId, selectedPackageKey)}
                                  >
                                    {saving ? 'Saving…' : changeOption?.label || 'Apply'}
                                  </button>
                                )}
                                {site.subscriptionChangeRequest && canManageSubscription && (
                                  <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => cancelScheduledChange(site.siteId)}
                                  >
                                    {saving ? 'Saving…' : 'Cancel scheduled change'}
                                  </button>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#9ca3af', fontSize: 13 }}>No sites are assigned to this account yet.</div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <div style={{ padding: 16, borderRadius: 12, border: '1px solid #374151', background: '#111827' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Saved Invoices</div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#9ca3af' }}>
                Download the invoices already generated for this billing account.
              </div>
              {billingProfile.invoiceDocuments?.length ? (
                <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                  {billingProfile.invoiceDocuments.map((documentEntry) => (
                    <div key={documentEntry.id} style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: '#0f172a', display: 'grid', gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>{documentEntry.referenceNumber || documentEntry.fileName || 'Stored invoice'}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                        {formatIsoDate(documentEntry.issueDate)} • {formatVnd(documentEntry.totalWithVat || 0)}
                      </div>
                      <div>
                        <button type="button" disabled={!documentEntry.downloadUrl} onClick={() => openDownloadUrl(documentEntry.downloadUrl)}>
                          Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>No invoices have been generated yet.</div>
              )}
            </div>

            <div style={{ padding: 16, borderRadius: 12, border: '1px solid #374151', background: '#111827' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Saved Contracts</div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#9ca3af' }}>
                Download your latest stored service agreements at any time.
              </div>
              {billingProfile.contractDocuments?.length ? (
                <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                  {billingProfile.contractDocuments.map((documentEntry) => (
                    <div key={documentEntry.id} style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: '#0f172a', display: 'grid', gap: 6 }}>
                      <div style={{ fontWeight: 600 }}>{documentEntry.referenceNumber || documentEntry.fileName || 'Stored contract'}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                        {documentEntry.templateLabel || 'Contract'} • {formatIsoDate(documentEntry.contractStartDate)} • {formatVnd(documentEntry.totalWithVat || 0)}
                      </div>
                      <div>
                        <button type="button" disabled={!documentEntry.downloadUrl} onClick={() => openDownloadUrl(documentEntry.downloadUrl)}>
                          Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>No contracts have been generated yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MySubscriptionSettings;
